import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import api, { apiError } from "@/lib/api";
import { SectionTitle } from "@/components/common";
import { Modal, Row, Field, Select } from "@/pages/Notizie";
import { useFormErrors, V, errSummary } from "@/lib/formErrors";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Users, KeyRound, Power, Pencil, ShieldCheck, ShieldAlert, Crown, DollarSign, Trash2, Mail, Archive, Check, X, CalendarClock } from "lucide-react";

const ROLE_LABEL = {
  direzione: "Socio",
  segreteria: "Segreteria",
  direttore_commerciale: "Direttore Commerciale",
  agente: "Agente",
  buyer: "Buyer",
  fotografo: "Fotografo",
};
const ROLE_CLS = {
  direzione: "text-[#FC82ED] bg-[#FC82ED]/10 border-[#FC82ED]/30",
  segreteria: "text-violet-600 bg-violet-500/10 border-violet-500/30",
  direttore_commerciale: "text-[#38BDF8] bg-[#38BDF8]/10 border-[#38BDF8]/30",
  agente: "text-slate-600 bg-slate-100 border-slate-300",
  buyer: "text-amber-600 bg-amber-400/10 border-amber-400/30",
};

export default function Utenti() {
  const { user, isDirezione, isDirettore, isSegreteria, logout } = useAuth();
  const isSegAdmin = isSegreteria && !isDirezione;
  const [ownerTransfer, setOwnerTransfer] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // {mode:'create'} | {mode:'edit', u}
  const [resetU, setResetU] = useState(null);
  const [delU, setDelU] = useState(null);
  const [archiveU, setArchiveU] = useState(null);
  const [transferU, setTransferU] = useState(null);
  const [diagU, setDiagU] = useState(null);

  const isOwner = user?.is_owner;
  const [teams, setTeams] = useState([]);
  const [ferieU, setFerieU] = useState(null);
  const load = () => api.get("/users").then((r) => { setItems(r.data); setLoading(false); }).catch(() => setLoading(false));
  const loadTeams = () => api.get("/teams").then((r) => setTeams(r.data)).catch(() => {});
  useEffect(() => { load(); loadTeams(); }, []);

  const toggle = async (u) => {
    try {
      const { data } = await api.post(`/users/${u.id}/toggle-active`);
      toast.success(data.active ? "Utente riattivato" : "Utente disattivato");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const resendWelcome = async (u) => {
    try {
      const { data } = await api.post(`/users/${u.id}/resend-welcome`);
      if (data?.bloccato) toast.warning(`Bloccato (anteprima): nessuna email a ${u.email}`);
      else toast.success(isSegAdmin ? `Invito inviato a ${u.email}` : `Email di primo accesso inviata a ${u.email}`);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="fade-up">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <SectionTitle sub={isDirettore ? "Consulta gli account del tuo team. Le modifiche agli account sono riservate alla Direzione." : "Crea e gestisci gli account del team. Ogni utente accede con credenziali personali e permessi in base al ruolo."}>Utenti & Team</SectionTitle>
        {!isDirettore && <button data-testid="new-user-btn" onClick={() => setModal({ mode: "create" })} className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#FC82ED] hover:bg-[#e06dd2] text-black font-semibold text-sm glow-pink transition-all"><Plus size={16} /> Nuovo Utente</button>}
      </div>

      {isDirezione && <DuplicateNamesWarning items={items} />}
      {isDirezione && <TwoFaPolicyCard />}
      {isDirezione && <TeamsCard teams={teams} onChanged={loadTeams} />}

      {loading ? <div className="space-y-2">{[...Array(5)].map((_, i) => <div key={i} className="skeleton h-16 rounded-xl" />)}</div> :
      items.length === 0 ? (
        <div className="card-immo p-12 text-center"><Users className="mx-auto text-[var(--muted)] mb-3" size={32} /><p className="text-[var(--muted)] text-sm">Nessun utente da mostrare.</p></div>
      ) : (
        <div className="card-immo overflow-hidden p-0">
          <div className="hidden lg:grid grid-cols-[1fr_180px_160px_130px_150px] gap-4 px-4 py-3 border-b border-[var(--border)] bg-[#F7F7F8] text-[10px] font-bold uppercase tracking-wider text-[var(--muted)]">
            <div>Utente</div><div>Ruolo</div><div>Team</div><div>Stato</div><div className="text-right">Azioni</div>
          </div>
          <div className="divide-y divide-[var(--border)]">
            {items.map((u) => (
              <div key={u.id} data-testid={`user-${u.id}`} className="grid lg:grid-cols-[1fr_180px_160px_130px_150px] gap-2 lg:gap-4 px-4 py-3 items-center hover:bg-slate-50/60 transition-colors">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-full bg-[#FC82ED]/15 border border-[#FC82ED]/30 flex items-center justify-center text-[#FC82ED] font-bold text-sm shrink-0">{(u.name || "?").slice(0, 1)}</div>
                  <div className="min-w-0">
                    <div className="font-semibold text-sm flex items-center gap-1.5 truncate">{u.name}{u.is_owner && <Crown size={13} className="text-[#FFB020]" title="Owner" />}</div>
                    <div className="text-[11px] text-[var(--muted)] truncate">{u.email}</div>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  {(u.roles && u.roles.length ? u.roles : [u.role]).map((r) => (
                    <span key={r} data-testid={`role-badge-${u.id}-${r}`} className={`inline-flex w-fit items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide border ${ROLE_CLS[r] || ROLE_CLS.agente}`}>{ROLE_LABEL[r] || r}</span>
                  ))}
                  {u.archived_shared && <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase border text-slate-500 bg-slate-100 border-slate-300">Storico</span>}
                  {u.role === "direzione" && !u.is_segreteria && u.finance_authorized &&
                    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase border text-emerald-600 bg-emerald-400/10 border-emerald-400/30"><DollarSign size={9} />Finanza</span>}
                </div>
                <div className="text-xs text-slate-600">{u.team_name || <span className="text-[var(--muted)]">—</span>}</div>
                <div className="flex flex-col gap-1">
                  <span className={`inline-flex w-fit items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase border ${u.active ? "text-emerald-600 bg-emerald-400/10 border-emerald-400/30" : "text-slate-500 bg-slate-100 border-slate-300"}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${u.active ? "bg-emerald-500" : "bg-slate-400"}`} /> {u.active ? "Attivo" : "Disattivato"}
                  </span>
                </div>
                <div className="flex items-center justify-start lg:justify-end gap-1">
                  {!isDirettore && <button data-testid={`edit-user-${u.id}`} onClick={() => setModal({ mode: "edit", u })} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-[#111111]" title="Modifica"><Pencil size={15} /></button>}
                  {!isDirettore && <button data-testid={`reset-user-${u.id}`} onClick={() => setResetU(u)} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-[#FC82ED]" title="Reimposta password"><KeyRound size={15} /></button>}
                  {((isOwner && (u.role === "segreteria" || (u.role === "direzione" && u.is_segreteria))) || isSegAdmin) && (
                    <button data-testid={`resend-welcome-${u.id}`} onClick={() => resendWelcome(u)} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-[#FC82ED]" title={isSegAdmin ? "Invia / reinvia invito" : "Rimanda email di primo accesso"}><Mail size={15} /></button>
                  )}
                  {isDirezione && (u.role === "segreteria" || (u.roles || []).includes("segreteria") || u.is_segreteria) && (
                    <button data-testid={`ferie-user-${u.id}`} onClick={() => setFerieU(u)} className="p-2 rounded-lg hover:bg-amber-50 text-amber-600 hover:text-amber-700" title="Ferie / sostituzione"><CalendarClock size={15} /></button>
                  )}
                  {!isDirettore && !u.is_owner && u.id !== user.id && (!isSegAdmin || u.active) && (
                    <button data-testid={`toggle-user-${u.id}`} onClick={() => toggle(u)} className={`p-2 rounded-lg hover:bg-slate-100 ${u.active ? "text-slate-500 hover:text-red-500" : "text-emerald-600"}`} title={u.active ? "Disattiva" : "Riattiva"}><Power size={15} /></button>
                  )}
                  {isDirettore && <span className="text-[11px] text-[var(--muted)] italic pr-1">Sola lettura</span>}
                  {isDirezione && (
                    <button data-testid={`perm-diag-${u.id}`} onClick={() => setDiagU(u)} className="p-2 rounded-lg hover:bg-sky-50 text-sky-600 hover:text-sky-700" title="Verifica permessi"><ShieldCheck size={15} /></button>
                  )}
                  {isOwner && u.is_owner && u.id === user.id && (
                    <button data-testid="owner-transfer-btn" onClick={() => setOwnerTransfer(true)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-amber-400 bg-amber-50 text-amber-700 hover:bg-amber-100 text-xs font-semibold"><Crown size={14} /> Trasferisci proprietà</button>
                  )}
                  {isDirezione && (u.roles || [u.role]).includes("direzione") && !u.is_owner && u.id === user.id && !u.archived_shared && (
                    <button data-testid={`archive-self-${u.id}`} onClick={() => setArchiveU(u)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-amber-400 bg-amber-50 text-amber-700 hover:bg-amber-100 text-xs font-semibold"><Archive size={14} /> Archivia</button>
                  )}
                  {isOwner && (u.roles || [u.role]).includes("direzione") && u.id !== user.id && u.active && !u.archived_shared && (
                    <button data-testid={`transfer-owner-${u.id}`} onClick={() => setTransferU(u)} className="p-2 rounded-lg hover:bg-amber-50 text-[#FFB020] hover:text-amber-600" title="Trasferisci proprietà"><Crown size={15} /></button>
                  )}
                  {isDirezione && (u.roles || [u.role]).includes("direzione") && !u.is_owner && u.id !== user.id && !u.archived_shared && (
                    <button data-testid={`archive-shared-${u.id}`} onClick={() => setArchiveU(u)} className="p-2 rounded-lg hover:bg-amber-50 text-amber-600 hover:text-amber-700" title="Archivia account condiviso"><Archive size={15} /></button>
                  )}
                  {isDirezione && !u.is_owner && u.id !== user.id && (
                    <button data-testid={`delete-user-${u.id}`} onClick={() => setDelU(u)} className="p-2 rounded-lg hover:bg-red-50 text-red-500 hover:text-red-600" title="Elimina definitivamente"><Trash2 size={15} /></button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {modal && <UserModal mode={modal.mode} u={modal.u} isOwner={isOwner} actor={user} teams={teams} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
      {resetU && <ResetModal u={resetU} onClose={() => setResetU(null)} onSaved={() => setResetU(null)} />}
      {delU && <DeleteModal u={delU} onClose={() => setDelU(null)} onDeleted={() => { setDelU(null); load(); }} />}
      {archiveU && <ArchiveModal u={archiveU} onClose={() => setArchiveU(null)} onDone={() => { setArchiveU(null); load(); }} onLogout={logout} />}
      {transferU && <TransferModal u={transferU} onClose={() => setTransferU(null)} />}
      {ownerTransfer && <OwnerTransferModal soci={items.filter((x) => (x.roles || [x.role]).includes("direzione") && x.id !== user.id && x.active && !x.archived_shared)} onClose={() => setOwnerTransfer(false)} />}
      {diagU && <PermDiagModal u={diagU} onClose={() => setDiagU(null)} />}
      {ferieU && <FerieModal u={ferieU} segreterie={items.filter((x) => x.role === "segreteria" || (x.roles || []).includes("segreteria") || x.is_segreteria)} onClose={() => setFerieU(null)} />}
    </div>
  );
}

function DuplicateNamesWarning({ items }) {
  const counts = {};
  (items || []).forEach((u) => {
    const key = (u.name || "").trim().toLowerCase();
    if (key) counts[key] = (counts[key] || 0) + 1;
  });
  const dups = Object.entries(counts).filter(([, n]) => n > 1);
  if (!dups.length) return null;
  return (
    <div className="card-immo p-4 mb-5 border-amber-300 bg-amber-50/60" data-testid="duplicate-names-warning">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-xl bg-amber-500/15 border border-amber-400/40 flex items-center justify-center text-amber-600 shrink-0"><ShieldAlert size={17} /></div>
        <div className="text-sm">
          <div className="font-semibold text-amber-800">Attenzione: nomi duplicati</div>
          <p className="text-amber-700 mt-0.5">Esistono più utenti con lo stesso nome e cognome. Verifica che non siano account doppi (es. lo stesso socio presente anche come agente):</p>
          <ul className="mt-1.5 space-y-0.5">
            {dups.map(([name, n]) => {
              const who = (items || []).filter((u) => (u.name || "").trim().toLowerCase() === name);
              return (
                <li key={name} className="text-amber-800" data-testid={`dup-name-${name.replace(/\s+/g, '-')}`}>
                  <b>{who[0]?.name}</b> ×{n} — {who.map((u) => `${u.email} (${(u.roles && u.roles.length ? u.roles : [u.role]).map((r) => ROLE_LABEL[r] || r).join("/")})`).join(", ")}
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
}

function TeamsCard({ teams, onChanged }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");

  const create = async () => {
    if (!name.trim()) return toast.error("Inserisci il nome del team");
    setBusy(true);
    try { await api.post("/teams", { name: name.trim() }); setName(""); toast.success("Team creato"); onChanged(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const rename = async (t) => {
    if (!editName.trim()) return;
    try { await api.put(`/teams/${t.team_id}`, { name: editName.trim() }); setEditId(null); toast.success("Team rinominato"); onChanged(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const setArchived = async (t, archived) => {
    try { await api.post(`/teams/${t.team_id}/archive`, { archived }); toast.success(archived ? "Team archiviato" : "Team ripristinato"); onChanged(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="card-immo p-5 mb-5" data-testid="teams-card">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-[#38BDF8]/15 border border-[#38BDF8]/30 flex items-center justify-center text-[#38BDF8] shrink-0"><Users size={18} /></div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-sm">Team</div>
          <p className="text-sm text-slate-500 mt-0.5">Crea e gestisci i team. I nuovi team compaiono subito nel modulo utente e tra i "Team seguiti dalla segreteria".</p>
        </div>
      </div>
      <div className="flex gap-2 mb-4">
        <input data-testid="team-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Nome nuovo team (es. Forze Speciali)"
               onKeyDown={(e) => e.key === "Enter" && create()}
               className="flex-1 px-3 py-2.5 rounded-lg bg-white border border-[var(--border)] focus:border-[#38BDF8] outline-none text-sm" />
        <button data-testid="create-team-btn" onClick={create} disabled={busy}
                className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-[#111111] hover:bg-black text-white font-semibold text-sm disabled:opacity-60"><Plus size={15} /> Crea team</button>
      </div>
      {(teams || []).length === 0 ? (
        <p className="text-sm text-[var(--muted)]">Nessun team. Creane uno qui sopra.</p>
      ) : (
        <div className="divide-y divide-[var(--border)] border-t border-[var(--border)]">
          {(teams || []).map((t) => (
            <div key={t.team_id} data-testid={`team-row-${t.team_id}`} className="flex items-center gap-3 py-2.5">
              <div className="flex-1 min-w-0">
                {editId === t.team_id ? (
                  <div className="flex items-center gap-2">
                    <input data-testid={`rename-team-input-${t.team_id}`} value={editName} autoFocus onChange={(e) => setEditName(e.target.value)}
                           onKeyDown={(e) => e.key === "Enter" && rename(t)}
                           className="px-2 py-1 rounded-lg bg-white border border-[#38BDF8] outline-none text-sm" />
                    <button data-testid={`rename-save-${t.team_id}`} onClick={() => rename(t)} className="p-1.5 rounded-lg text-emerald-600 hover:bg-emerald-50"><Check size={15} /></button>
                    <button onClick={() => setEditId(null)} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100"><X size={15} /></button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm truncate">{t.team_name}</span>
                    {t.archived && <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase border text-slate-500 bg-slate-100 border-slate-300">Archiviato</span>}
                  </div>
                )}
                <div className="text-[11px] text-[var(--muted)] mt-0.5 truncate">
                  Direttore: {t.direttore || "—"} · {t.membri_attivi}/{t.membri_totali} membri attivi
                </div>
              </div>
              {!t.archived && (
                <>
                  <button data-testid={`rename-team-${t.team_id}`} onClick={() => { setEditId(t.team_id); setEditName(t.team_name); }} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-[#111111]" title="Rinomina"><Pencil size={15} /></button>
                  <button data-testid={`archive-team-${t.team_id}`} onClick={() => setArchived(t, true)} disabled={t.membri_attivi > 0}
                          className="p-2 rounded-lg hover:bg-amber-50 text-amber-600 disabled:opacity-40 disabled:cursor-not-allowed" title={t.membri_attivi > 0 ? "Ha membri attivi" : "Archivia"}><Archive size={15} /></button>
                </>
              )}
              {t.archived && (
                <button data-testid={`restore-team-${t.team_id}`} onClick={() => setArchived(t, false)} className="p-2 rounded-lg hover:bg-emerald-50 text-emerald-600" title="Ripristina"><Power size={15} /></button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const TWOFA_ROLES = [
  ["direzione", "Direzione"],
  ["segreteria", "Segreteria"],
  ["direttore_commerciale", "Direttore Commerciale"],
  ["agente", "Agente"],
];

function TwoFaPolicyCard() {
  const [roles, setRoles] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/settings/security").then((r) => setRoles(r.data.twofa_required_roles || [])).catch(() => setRoles([])); }, []);
  const toggle = (r) => setRoles((cur) => cur.includes(r) ? cur.filter((x) => x !== r) : [...cur, r]);
  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put("/settings/security", { twofa_required_roles: roles }); setRoles(data.twofa_required_roles || []); toast.success("Policy 2FA aggiornata"); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  const anyOn = (roles || []).length > 0;
  return (
    <div className="card-immo p-5 mb-5" data-testid="twofa-policy-card">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-[#FC82ED]/15 border border-[#FC82ED]/30 flex items-center justify-center text-[#FC82ED] shrink-0"><ShieldCheck size={18} /></div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-sm">Autenticazione a due fattori (2FA)</div>
          <p className="text-sm text-slate-500 mt-0.5">{anyOn ? "La 2FA è richiesta al login per i ruoli selezionati (in blu)." : "La 2FA è disattivata: tutti accedono con email e password."} Attivala o disattivala per ruolo quando vuoi, senza modifiche al codice.</p>
        </div>
      </div>
      <div className="mt-4 border-t border-slate-100 pt-4 flex flex-wrap gap-2">
        {roles === null ? <span className="text-sm text-[var(--muted)]">Caricamento…</span> : TWOFA_ROLES.map(([key, label]) => {
          const on = roles.includes(key);
          return (
            <button key={key} data-testid={`twofa-role-${key}`} onClick={() => toggle(key)}
                    className={`px-3 py-1.5 rounded-xl text-sm font-medium border transition-all ${on ? "bg-[#FC82ED] text-black border-transparent" : "bg-white text-slate-600 border-[var(--border)] hover:bg-slate-50"}`}>
              {label} · {on ? "2FA ON" : "OFF"}
            </button>
          );
        })}
      </div>
      {roles !== null && (
        <div className="mt-4 flex justify-end">
          <button data-testid="twofa-policy-save" onClick={save} disabled={saving}
                  className="px-4 py-2 rounded-xl bg-[#FC82ED] hover:bg-[#e06dd2] text-black font-semibold text-sm disabled:opacity-60">{saving ? "Salvataggio…" : "Salva policy 2FA"}</button>
        </div>
      )}
    </div>
  );
}

const ALL_ROLES = [
  ["direzione", "Socio (Direzione)"],
  ["segreteria", "Segreteria"],
  ["direttore_commerciale", "Direttore Commerciale"],
  ["agente", "Agente"],
  ["buyer", "Buyer"],
  ["fotografo", "Fotografo"],
];

function allowedRoleKeys(isOwner, actor) {
  if (actor?.role === "direttore_commerciale") return ["agente", "buyer"];
  if (isOwner || actor?.role === "direzione") return ["direzione", "segreteria", "direttore_commerciale", "agente", "buyer", "fotografo"];
  return ["direttore_commerciale", "agente", "buyer"];
}

function UserModal({ mode, u, isOwner, actor, teams, onClose, onSaved }) {
  const editing = mode === "edit";
  const initRoles = editing
    ? (u.roles && u.roles.length ? u.roles : [u.role])
    : [];
  const [f, setF] = useState({
    name: u?.name || "",
    email: u?.email || "",
    telefono: u?.telefono || "",
    roles: initRoles,
    team_name: u?.team_name || "",
    team_ids: u?.team_ids || [],
    password: "",
    sede: u?.sede || "",
    cciaa: u?.cciaa || "",
    rea: u?.rea || "",
  });
  const [creatingTeam, setCreatingTeam] = useState(false);
  const [saving, setSaving] = useState(false);
  const allowed = allowedRoleKeys(isOwner, actor);
  const { errors, clear, validate, applyServer, summary } = useFormErrors();
  const set = (k) => (e) => { setF({ ...f, [k]: e.target.value }); clear(k); };

  const hasRole = (r) => f.roles.includes(r);
  const toggleRole = (r) => setF((cur) => ({ ...cur, roles: cur.roles.includes(r) ? cur.roles.filter((x) => x !== r) : [...cur.roles, r] }));

  const operative = f.roles.some((r) => ["direttore_commerciale", "agente", "buyer"].includes(r));
  const showTeam = operative && actor?.role !== "direttore_commerciale";
  const segAdmin = !!actor && (actor.role === "segreteria" || actor.is_segreteria) && actor.role !== "direzione";
  const showSegTeams = hasRole("segreteria");

  const teamOpts = segAdmin
    ? [["", "— Scegli il team —"], ...(teams || []).filter((t) => !t.archived).map((t) => [t.team_name, t.team_name])]
    : [["", "— Nessun team (lavora da solo) —"], ...(teams || []).filter((t) => !t.archived).map((t) => [t.team_name, t.team_name]), ["__new__", "＋ Crea nuovo team…"]];
  const onTeamChange = (e) => {
    const v = e.target.value;
    if (v === "__new__") { setCreatingTeam(true); setF({ ...f, team_name: "" }); }
    else { setCreatingTeam(false); setF({ ...f, team_name: v }); }
  };
  const toggleSegTeam = (tid) => setF((cur) => ({ ...cur, team_ids: cur.team_ids.includes(tid) ? cur.team_ids.filter((x) => x !== tid) : [...cur.team_ids, tid] }));

  const isOwnerTarget = editing && !!u?.is_owner;

  const buildPayload = () => {
    const p = { name: f.name.trim(), roles: f.roles, telefono: f.telefono.trim() };
    if (f.roles.includes("direzione")) p.finance_authorized = true;
    if (operative) p.team_name = f.team_name;
    if (f.roles.includes("segreteria")) p.team_ids = f.team_ids;
    if (editing && !isOwnerTarget && f.email.trim()) p.email = f.email.trim();
    return p;
  };

  const save = async () => {
    const rules = editing ? { name: [V.required], email: [V.email] } : { name: [V.required], email: [V.required, V.email], password: [V.password] };
    const errs = validate(rules, f);
    if (Object.keys(errs).length) return toast.error(errSummary(Object.keys(errs).length));
    if (!f.roles.length) return toast.error("Seleziona almeno un ruolo");
    if (showTeam && creatingTeam && !f.team_name.trim()) return toast.error("Inserisci il nome del nuovo team");
    if (segAdmin && !f.team_name) return toast.error("Scegli il team");
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/users/${u.id}`, buildPayload());
        if (actor?.role === "direzione") await api.put(`/users/${u.id}/dati-professionali`, { sede: f.sede, cciaa: f.cciaa, rea: f.rea });
        toast.success("Utente aggiornato");
      } else {
        await api.post("/users", { ...buildPayload(), email: f.email.trim(), password: f.password });
        toast.success("Utente creato");
      }
      onSaved();
    } catch (e) {
      if (applyServer(e)) return;
      const d = e.response?.data?.detail;
      toast.error(typeof d === "object" && d?.message ? d.message : apiError(e));
    } finally { setSaving(false); }
  };

  return (
    <Modal title={editing ? "Modifica Utente" : "Nuovo Utente"} onClose={onClose} onSave={save} saving={saving} testid="user-modal" summary={summary} scheda value={f} onRestore={setF}>
      <Row>
        <Field label="Nome e Cognome" testid="user-name" required error={errors.name} value={f.name} onChange={set("name")} />
        {editing
          ? <div>
              <Field label="Email" testid="user-email" error={errors.email} value={f.email} onChange={set("email")} disabled={isOwnerTarget} />
              {isOwnerTarget && <p className="text-[11px] text-[var(--muted)] mt-1">Email owner legata all'identità di sistema (ADMIN_EMAIL): modificabile solo dai Secret di deployment.</p>}
            </div>
          : <Field label="Email" testid="user-email" required error={errors.email} value={f.email} onChange={set("email")} />}
      </Row>
      <Field label="Telefono" testid="user-telefono" type="tel" value={f.telefono} onChange={set("telefono")} />
      <div>
        <label className="block text-[11px] font-semibold uppercase tracking-wide text-[var(--muted)] mb-1.5">Ruoli <span className="text-red-500">*</span></label>
        <div className="flex flex-wrap gap-2" data-testid="user-roles">
          {ALL_ROLES.filter(([k]) => allowed.includes(k)).map(([k, label]) => {
            const on = hasRole(k);
            return (
              <button key={k} type="button" data-testid={`role-opt-${k}`} aria-pressed={on} disabled={isOwnerTarget}
                      onClick={() => toggleRole(k)}
                      className={`px-3 py-1.5 rounded-xl text-sm font-medium border transition-all disabled:opacity-60 ${on ? "bg-[#FC82ED] text-black border-transparent" : "bg-white text-slate-600 border-[var(--border)] hover:bg-slate-50"}`}>
                {label}
              </button>
            );
          })}
        </div>
        {isOwnerTarget && <p className="text-[11px] text-[var(--muted)] mt-1">Ruolo owner (Direzione + Finanza): non modificabile.</p>}
        <p className="text-[11px] text-[var(--muted)] mt-1.5">Puoi assegnare più ruoli (es. Socio + Direttore). Chi ha più ruoli troverà in alto il selettore di vista.</p>
      </div>
      {showTeam && (
        <div>
          <Select label={segAdmin ? "Team *" : "Team (per Direttore/Agente/Buyer)"} testid="user-team" error={errors.team_name} value={creatingTeam ? "__new__" : f.team_name} onChange={onTeamChange} opts={teamOpts} />
          {creatingTeam && (
            <div className="mt-2">
              <Field label="Nome nuovo team" testid="user-new-team" value={f.team_name} onChange={set("team_name")} />
            </div>
          )}
        </div>
      )}
      {showSegTeams && (
        <div>
          <label className="block text-[11px] font-semibold uppercase tracking-wide text-[var(--muted)] mb-1.5">Team seguiti dalla Segreteria ("I miei team")</label>
          <div className="flex flex-wrap gap-2" data-testid="user-seg-teams">
            {(teams || []).filter((t) => !t.archived).length === 0 ? <span className="text-xs text-[var(--muted)]">Nessun team disponibile.</span> : (teams || []).filter((t) => !t.archived).map((t) => {
              const on = f.team_ids.includes(t.team_id);
              return (
                <button key={t.team_id} type="button" data-testid={`seg-team-${t.team_id}`} aria-pressed={on} onClick={() => toggleSegTeam(t.team_id)}
                        className={`px-3 py-1.5 rounded-xl text-sm font-medium border transition-all ${on ? "bg-violet-500 text-white border-transparent" : "bg-white text-slate-600 border-[var(--border)] hover:bg-slate-50"}`}>
                  {t.team_name}
                </button>
              );
            })}
          </div>
        </div>
      )}
      {actor?.role === "direttore_commerciale" && (
        <div className="text-[11px] text-[var(--muted)] flex items-center gap-1.5"><ShieldCheck size={12} /> Verrà assegnato al tuo team ({actor.team_name}).</div>
      )}
      {editing && actor?.role === "direzione" && (
        <div data-testid="user-dati-professionali">
          <label className="block text-[11px] font-semibold uppercase tracking-wide text-[var(--muted)] mb-1.5">Dati professionali (usati nelle proposte)</label>
          <Row>
            <Field label="Sede" testid="user-sede" value={f.sede} onChange={set("sede")} />
            <Field label="CCIAA" testid="user-cciaa" value={f.cciaa} onChange={set("cciaa")} />
          </Row>
          <div className="mt-3"><Field label="Numero REA" testid="user-rea" value={f.rea} onChange={set("rea")} /></div>
        </div>
      )}
      {!editing && (
        <>
          <Field label="Password iniziale" testid="user-password" type="password" required error={errors.password} value={f.password} onChange={set("password")} />
          <div className="text-[11px] text-[var(--muted)]">Min. 12 caratteri con almeno 3 tra: minuscole, maiuscole, numeri, simboli. L'utente dovrà cambiarla al primo accesso.</div>
        </>
      )}
    </Modal>
  );
}

function ResetModal({ u, onClose, onSaved }) {
  const [pw, setPw] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/users/${u.id}/reset-password`, { password: pw });
      toast.success(`Password reimpostata per ${u.name}`);
      onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Modal title={`Reimposta password · ${u.name}`} onClose={onClose} onSave={save} saving={saving} testid="reset-pw-modal" saveLabel="Reimposta">
      <Field label="Nuova password" testid="reset-pw-input" type="password" value={pw} onChange={(e) => setPw(e.target.value)} />
      <div className="text-[11px] text-[var(--muted)]">Min. 8 caratteri con lettere e numeri. L'utente dovrà cambiarla al primo accesso.</div>
    </Modal>
  );
}

function PermDiagModal({ u, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    api.get(`/users/${u.id}/permessi-diagnostica`).then((r) => setData(r.data)).catch((e) => setErr(apiError(e)));
  }, [u.id]);
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="perm-diag-modal">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg max-h-[calc(100vh-2rem)] overflow-y-auto card-immo p-6">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-sky-500/10 border border-sky-400/30 flex items-center justify-center text-sky-600 shrink-0"><ShieldCheck size={20} /></div>
          <div>
            <h3 className="font-display text-lg font-bold">Verifica permessi</h3>
            <p className="text-sm text-slate-600 mt-0.5">{u.name} · {u.email}</p>
          </div>
        </div>
        {err && <div className="text-sm text-red-600">{err}</div>}
        {!data && !err && <div className="text-sm text-[var(--muted)]">Caricamento…</div>}
        {data && (
          <>
            <div className="grid grid-cols-2 gap-2 mb-4 text-sm">
              <div className="card-immo p-3"><div className="text-[11px] uppercase text-[var(--muted)]">Ruoli</div><div className="font-semibold" data-testid="diag-roles">{(data.user.roles || []).map((r) => ROLE_LABEL[r] || r).join(", ")}</div></div>
              <div className="card-immo p-3"><div className="text-[11px] uppercase text-[var(--muted)]">Vista attiva</div><div className="font-semibold">{data.user.active_view || "—"}</div></div>
              <div className="card-immo p-3"><div className="text-[11px] uppercase text-[var(--muted)]">Ruolo effettivo</div><div className="font-semibold" data-testid="diag-effective">{ROLE_LABEL[data.user.role_effettivo] || data.user.role_effettivo}</div></div>
              <div className="card-immo p-3"><div className="text-[11px] uppercase text-[var(--muted)]">Vede tutta l'agenzia</div><div className={`font-semibold ${data.user.vede_tutta_agenzia ? "text-emerald-600" : "text-amber-600"}`} data-testid="diag-fullscope">{data.user.vede_tutta_agenzia ? "Sì" : "No (solo i suoi)"}</div></div>
            </div>
            <div className="border-t border-[var(--border)] pt-2">
              <div className="text-[11px] uppercase tracking-wide text-[var(--muted)] mb-1.5">Record visti / totale agenzia</div>
              {data.sezioni.map((s) => {
                const full = s.visti === s.totali;
                return (
                  <div key={s.nome} data-testid={`diag-sec-${s.nome}`} className="flex items-center justify-between py-1.5 text-sm border-b border-[var(--border)] last:border-0">
                    <span>{s.nome}</span>
                    <span className={`font-mono font-semibold ${full ? "text-emerald-600" : "text-amber-600"}`}>{s.visti} / {s.totali}</span>
                  </div>
                );
              })}
            </div>
            {!data.user.vede_tutta_agenzia && (data.user.roles || []).includes("direzione") && (
              <p className="text-[12px] text-amber-700 mt-3">Questo utente ha il ruolo Direzione ma la vista attiva non è "direzione": usa il selettore di vista o riaprilo per correggere.</p>
            )}
            {!(data.user.roles || []).includes("direzione") && (
              <p className="text-[12px] text-amber-700 mt-3">Questo account NON ha il ruolo Direzione (è {(data.user.roles || []).map((r) => ROLE_LABEL[r] || r).join("/")}). Se dovrebbe essere un Socio, potrebbe essere un account duplicato: aggiungi il ruolo Direzione o usa l'account corretto.</p>
            )}
          </>
        )}
        <div className="mt-4"><button onClick={onClose} className="w-full py-2.5 rounded-xl bg-[#F7F7F8] border border-[var(--border)] text-sm font-medium hover:bg-slate-100">Chiudi</button></div>
      </div>
    </div>,
    document.body
  );
}

function OwnerTransferModal({ soci, onClose }) {
  const [sel, setSel] = useState("");
  const u = soci.find((s) => s.id === sel);
  return (
    <>
      {!u && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="owner-transfer-pick-modal">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
          <div className="relative w-full max-w-md card-immo p-6">
            <h3 className="font-display text-lg font-bold mb-1">Trasferisci proprietà</h3>
            <p className="text-sm text-slate-600 mb-3">Scegli il Socio che diventerà proprietario (corona).</p>
            {soci.length === 0 ? <p className="text-sm text-red-600" data-testid="owner-transfer-no-soci">Nessun altro Socio attivo: crea prima un account Socio personale.</p> : (
              <select data-testid="owner-transfer-select" value={sel} onChange={(e) => setSel(e.target.value)} className="w-full px-3 py-2.5 rounded-xl border border-[var(--border)] text-base sm:text-sm bg-white">
                <option value="">— Scegli il Socio —</option>
                {soci.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.email})</option>)}
              </select>
            )}
            <button onClick={onClose} className="w-full mt-4 py-2.5 rounded-xl bg-[#F7F7F8] border border-[var(--border)] text-sm font-medium hover:bg-slate-100">Annulla</button>
          </div>
        </div>, document.body)}
      {u && <TransferModal u={u} onClose={onClose} />}
    </>
  );
}

function TransferModal({ u, onClose }) {
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const go = async () => {
    setSaving(true);
    try {
      await api.post(`/users/${u.id}/transfer-ownership`, { confirm });
      toast.success(`Proprietà trasferita a ${u.name}`);
      setTimeout(() => window.location.reload(), 800);
    } catch (e) { toast.error(apiError(e)); setSaving(false); }
  };
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="transfer-owner-modal">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md max-h-[calc(100vh-2rem)] overflow-y-auto card-immo p-6">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-400/30 flex items-center justify-center text-[#FFB020] shrink-0"><Crown size={20} /></div>
          <div>
            <h3 className="font-display text-lg font-bold">Trasferisci proprietà</h3>
            <p className="text-sm text-slate-600 mt-0.5">La corona passa a <b>{u.name}</b> ({u.email}). Tu resti Socio, ma non sarai più il proprietario. L'operazione viene registrata nello storico.</p>
          </div>
        </div>
        <label className="text-xs font-semibold text-slate-600">Per confermare scrivi il nome o l'email di {u.name}</label>
        <input data-testid="transfer-owner-confirm-input" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder={u.name}
               className="w-full mt-1 mb-4 px-3 py-2.5 rounded-xl border border-[var(--border)] text-base sm:text-sm" />
        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl bg-[#F7F7F8] border border-[var(--border)] text-sm font-medium hover:bg-slate-100">Annulla</button>
          <button data-testid="transfer-owner-confirm" onClick={go} disabled={saving || !confirm.trim()}
                  className="flex-1 py-2.5 rounded-xl bg-[#111111] text-white font-semibold text-sm disabled:opacity-50">{saving ? "Trasferimento…" : "Trasferisci"}</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function ArchiveModal({ u, onClose, onDone, onLogout }) {
  const [saving, setSaving] = useState(false);
  const [chk, setChk] = useState(null);
  useEffect(() => { api.get(`/users/${u.id}/archive-check`).then((r) => setChk(r.data)).catch((e) => toast.error(apiError(e))); }, [u.id]);
  const go = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/users/${u.id}/archive-shared`, { sposta_a: chk?.sposta_a?.id });
      toast.success(`Account archiviato. Attività automatiche spostate a ${data.spostati_a}`);
      if (chk?.se_stesso) { setTimeout(() => onLogout?.(), 1200); return; }
      onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="archive-shared-modal">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md max-h-[calc(100vh-2rem)] overflow-y-auto card-immo p-6">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-400/30 flex items-center justify-center text-amber-600 shrink-0"><Archive size={20} /></div>
          <div>
            <h3 className="font-display text-lg font-bold">Archivia account condiviso</h3>
            <p className="text-sm text-slate-600 mt-0.5">Stai per archiviare <b>{u.name}</b> ({u.email}). Verrà rinominato <b>"Direzione Immobilart (account storico)"</b>, il login e le sessioni attive vengono chiusi e <b>tutti i dati e lo storico restano intatti</b>.</p>
          </div>
        </div>
        {!chk ? <div className="skeleton h-20 rounded-xl mb-4" /> : (
          <div className="mb-4 space-y-2 text-sm" data-testid="archive-check">
            {chk.se_stesso && <div data-testid="archive-self-note" className="p-2.5 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-xs">Stai archiviando l'account con cui sei connesso: dopo l'archiviazione verrai disconnesso. Rientra con il tuo account Socio personale.</div>}
            {chk.blocchi.map((b) => <div key={b} data-testid="archive-blocco" className="p-2.5 rounded-xl bg-red-50 border border-red-200 text-red-700 text-xs">{b}</div>)}
            <div className="p-3 rounded-xl bg-[#F7F7F8] border border-[var(--border)]">
              <div className="text-[11px] font-bold uppercase tracking-wide text-[var(--muted)] mb-1">Cosa usa ancora questo account</div>
              {chk.usi.length === 0 ? <div className="text-xs">Nessuna attività automatica.</div> :
                chk.usi.map((x) => <div key={x.cosa} className="text-xs flex justify-between gap-2"><span>{x.cosa}</span><b>{x.n}</b></div>)}
              {chk.usi.length > 0 && <div className="text-xs mt-2">Verranno spostati su <b data-testid="archive-sposta-a">{chk.sposta_a?.name || "—"}</b> (proprietario).</div>}
            </div>
            <p className="text-[11px] text-[var(--muted)]">{chk.nota_email} {chk.email_direzione ? `(${chk.email_direzione})` : ""}</p>
          </div>
        )}
        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl bg-[#F7F7F8] border border-[var(--border)] text-sm font-medium hover:bg-slate-100">Annulla</button>
          <button data-testid="archive-shared-confirm" onClick={go} disabled={saving || !chk || chk.blocchi.length > 0}
                  className="flex-1 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-semibold text-sm disabled:opacity-50 transition-colors">
            {saving ? "Archiviazione…" : "Archivia account"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function DeleteModal({ u, onClose, onDeleted }) {
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const c = confirm.trim().toLowerCase();
  const match = c && (c === (u.name || "").trim().toLowerCase() || c === (u.email || "").trim().toLowerCase());
  const del = async () => {
    if (!match) return;
    setSaving(true);
    try {
      await api.delete(`/users/${u.id}`, { data: { confirm: confirm.trim() } });
      toast.success("Utente eliminato definitivamente");
      onDeleted();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="delete-user-modal">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md max-h-[calc(100vh-2rem)] overflow-y-auto card-immo p-6">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-red-500/10 border border-red-400/30 flex items-center justify-center text-red-500 shrink-0"><Trash2 size={20} /></div>
          <div>
            <h3 className="font-display text-lg font-bold text-red-600">Elimina definitivamente</h3>
            <p className="text-sm text-slate-600 mt-0.5">Stai per eliminare <b>{u.name}</b> ({u.email}). L'operazione è <b>irreversibile</b>. Lo storico verrà conservato come "Utente eliminato" e i dati gestionali collegati non saranno cancellati.</p>
          </div>
        </div>
        <p className="text-[11px] font-semibold uppercase tracking-wide text-[var(--muted)] mb-1.5">Per confermare digita il nome o l'email dell'utente</p>
        <input data-testid="delete-confirm-input" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoFocus
               placeholder={u.email}
               className="w-full px-3 py-2.5 rounded-lg bg-white border border-[var(--border)] focus:border-red-400 outline-none text-sm mb-5 transition-colors" />
        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl bg-[#F7F7F8] border border-[var(--border)] text-sm font-medium hover:bg-slate-100">Annulla</button>
          <button data-testid="delete-confirm-btn" onClick={del} disabled={!match || saving}
                  className="flex-1 py-2.5 rounded-xl bg-red-500 hover:bg-red-600 text-white font-semibold text-sm disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
            Elimina definitivamente
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}


function FerieModal({ u, segreterie, onClose }) {
  const [dal, setDal] = useState("");
  const [al, setAl] = useState("");
  const [sost, setSost] = useState("");
  const [list, setList] = useState([]);
  const [saving, setSaving] = useState(false);
  const loadList = () => api.get(`/assenze?user_id=${u.id}`).then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { loadList(); }, [u.id]);
  const opts = (segreterie || []).filter((s) => s.id !== u.id);
  const salva = async () => {
    if (!dal || !al) return toast.error("Inserisci le date");
    setSaving(true);
    try {
      await api.post("/assenze", { user_id: u.id, dal, al, sostituto_id: sost || undefined });
      toast.success("Assenza registrata: le code passano alla sostituta, avvisati agenti e direttori");
      setDal(""); setAl(""); setSost(""); loadList();
    } catch (e) { toast.error(apiError(e) || "Errore"); } finally { setSaving(false); }
  };
  const elimina = async (id) => { try { await api.delete(`/assenze/${id}`); loadList(); } catch { toast.error("Errore"); } };
  return createPortal(
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="ferie-modal">
      <div className="bg-white rounded-2xl max-w-md w-full max-h-[85vh] overflow-auto p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div className="font-semibold text-sm">Ferie / sostituzione — {u.name}</div>
          <button data-testid="ferie-close" onClick={onClose} className="p-1 rounded-lg hover:bg-slate-100"><X size={16} /></button>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <label className="text-[11px] text-[var(--muted)]">Assente dal
            <input data-testid="ferie-dal" type="date" value={dal} onChange={(e) => setDal(e.target.value)} className="w-full mt-1 px-2 py-1.5 rounded-lg border border-[var(--border)] text-sm" />
          </label>
          <label className="text-[11px] text-[var(--muted)]">Al
            <input data-testid="ferie-al" type="date" value={al} onChange={(e) => setAl(e.target.value)} className="w-full mt-1 px-2 py-1.5 rounded-lg border border-[var(--border)] text-sm" />
          </label>
        </div>
        <label className="text-[11px] text-[var(--muted)] block mt-2">Sostituta
          <select data-testid="ferie-sostituto" value={sost} onChange={(e) => setSost(e.target.value)} className="w-full mt-1 px-2 py-1.5 rounded-lg border border-[var(--border)] text-sm">
            <option value="">Automatica (l'altra segretaria)</option>
            {opts.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </label>
        <button data-testid="ferie-salva" onClick={salva} disabled={saving} className="mt-3 w-full py-2 rounded-xl bg-[#FC82ED] text-black text-sm font-semibold disabled:opacity-60">{saving ? "Salvo…" : "Registra assenza"}</button>
        {list.length > 0 && (
          <div className="mt-4">
            <div className="text-[11px] font-bold uppercase tracking-wider text-[var(--muted)] mb-2">Assenze registrate</div>
            {list.map((a) => (
              <div key={a.id} data-testid={`ferie-riga-${a.id}`} className="flex items-center justify-between text-sm border-b border-[var(--border)] last:border-0 py-1.5">
                <span>{a.dal} → {a.al} · sost. <b>{a.sostituto_nome}</b></span>
                <button data-testid={`ferie-del-${a.id}`} onClick={() => elimina(a.id)} className="text-red-500 hover:text-red-600 p-1"><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>, document.body);
}

